import * as Yup from "yup";
import { partnerInfoSchemaWithClientCode } from "./shipmentschema";

/**
 * Validation for the Addresses and Partners directories (both use PartnerForm).
 *
 * These two are the source of truth for locations: once an address here carries
 * coordinates, every shipment that picks the company from the directory
 * inherits them. So the directory is where a Google Places selection is made
 * mandatory — the shipment form deliberately still allows a hand-typed address
 * so a job can be drafted quickly.
 *
 * Records saved before coordinates existed have none. Rather than a bulk
 * migration, editing such a record requires re-picking the address, so the data
 * heals as records are touched.
 *
 * The "a suggestion must be picked" rule itself lives in PartnerForm
 * (`enforceAddressPick`), not here — this schema only checks that whatever
 * coordinates arrive are well-formed, which is why they are `notRequired()`.
 *
 * That rule is deliberately absolute: it is NOT waived when Google Places is
 * unreachable or returns a place with no geometry. Those states change the hint
 * the form shows, never whether it saves. The backend rejects a directory write
 * without coordinates, so waiving it client-side would only trade an inline
 * message for a server error. The cost is real and accepted — while Places is
 * down, the directory cannot be edited.
 */
export const partnerDirectorySchema = partnerInfoSchemaWithClientCode.shape({
  address: Yup.object().shape({
    street: Yup.string().required("validation:address.streetRequired"),
    postalCode: Yup.string().nullable(),
    state: Yup.string().required("validation:address.stateRequired"),
    city: Yup.string().required("validation:address.cityRequired"),
    country: Yup.string().required("validation:address.countryRequired"),

    // Present only after a Places suggestion is chosen. The form clears these
    // the moment the address text is edited by hand, so "has coordinates"
    // means "the text on screen is the place that was picked".
    coordinates: Yup.object()
      .shape({
        latitude: Yup.number()
          .typeError("validation:address.selectFromSuggestions")
          .min(-90)
          .max(90)
          .required("validation:address.selectFromSuggestions"),
        longitude: Yup.number()
          .typeError("validation:address.selectFromSuggestions")
          .min(-180)
          .max(180)
          .required("validation:address.selectFromSuggestions"),
      })
      // `default(undefined)` keeps yup from casting a missing value to `{}`,
      // which would then fail the inner required checks.
      .default(undefined)
      .notRequired(),
  }),
});
